import { create } from "zustand";
import { toast } from "sonner";
import { useCreatorsStore } from "../creators/creatorsStore";
import { useCampaignStore } from "../campaign/campaignStore";
import type { EvePhasePatch, EveUpdateCampaignPhasesBody, EveUpdateCampaignPhasesOptions } from "@/types/eve";
import type { EveOfferOverride } from "@/types/types";

/**
 * Native Amy-v2 actions: approve/reject on eve-run campaigns go to the amy
 * service's Clerk-authed action routes (POST /eve/v1/actions/*), NEVER the
 * legacy Flask updateCreatorStatus (which 409s eve campaigns as a backstop).
 * Amy's follow-through email rides a work-order session server-side — the
 * 200 means "decision recorded, Amy is sending".
 */

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

interface EveActionResponse {
  ok: boolean;
  code?: string;
  error?: string;
  status?: string;
  detail?: string;
  /** What Amy does next (resolve-handoff): reply_email | manual_reply | clear_error | none. */
  next?: string;
  current_status?: string;
  decided?: "approved" | "rejected" | "revision" | "confirmed" | "declined" | "superseded" | null;
}

const DECIDED_LABELS: Record<string, string> = {
  approved: "Already approved",
  rejected: "Already rejected",
  revision: "Already sent back for revision",
  confirmed: "Payout already confirmed",
  declined: "Already declined",
  superseded: "Already closed — the creator signed meanwhile",
};

interface EveActionsState {
  isLoading: boolean;
  approveCreator: (
    campaignCreatorId: string,
    opts: { promo_code?: string; unique_link?: string },
    token: string
  ) => Promise<boolean>;
  rejectCreator: (campaignCreatorId: string, token: string) => Promise<boolean>;
  resolvePhaseReview: (
    campaignCreatorId: string,
    decision: "approve" | "revision",
    notes: string | undefined,
    token: string
  ) => Promise<boolean>;
  confirmPayout: (campaignCreatorId: string, token: string) => Promise<boolean>;
  /** Contract change request: approve with the (possibly edited) amendment wording — the ONLY door that puts wording into a contract. */
  approveContractChange: (campaignCreatorId: string, amendments: string[], token: string) => Promise<boolean>;
  /** Contract change request: keep the standard contract (Amy tells the creator and re-sends the link). */
  declineContractChange: (campaignCreatorId: string, token: string) => Promise<boolean>;
  cancelPayout: (campaignCreatorId: string, reason: string, token: string) => Promise<boolean>;
  /** True while a pipeline-config save is in flight (PipelineTab editors). */
  isSavingConfig: boolean;
  /**
   * Patch pipeline phases. Per-tier pipelines: `options.tiers` names the
   * partnership tiers the patches target (omitted/null = EVERY tier — the
   * shared-pipeline save, today's behavior); `options.copy_from_tier`
   * replaces each target tier's pipeline with a copy of that tier's (`phases`
   * may be null then).
   */
  updateCampaignPhases: (
    campaignId: string,
    phases: EvePhasePatch[] | null,
    orgId: string,
    token: string,
    options?: EveUpdateCampaignPhasesOptions
  ) => Promise<boolean>;
  setCampaignAutopilot: (
    campaignId: string,
    enabled: boolean,
    orgId: string,
    token: string
  ) => Promise<boolean>;
  setAmyInstructions: (
    campaignId: string,
    content: string,
    orgId: string,
    token: string
  ) => Promise<boolean>;
  /**
   * Manage the campaign's mailboxes: naming a pool mailbox (address or
   * connected account id) not yet on the campaign adds it; `enabled`
   * switches one off/on (`requeue_unreplied` re-queues a switched-off
   * mailbox's unanswered creators); `remove` takes one off (refused while
   * it holds creator threads).
   */
  setCampaignMailbox: (
    campaignId: string,
    change: { mailbox: string; enabled?: boolean; remove?: boolean; requeue_unreplied?: boolean },
    orgId: string,
    token: string,
    successMessage?: string
  ) => Promise<MailboxChangeResult | null>;
  /**
   * Tasks-page handoff dialog on eve rows: "answer" re-arms the creator and
   * has the subagent owning her status reply with the team's answer (Amy
   * saves knowledge only when the answer is a reusable fact); "dismiss" just
   * re-arms. Legacy rows keep the Flask handleHandoff / dismissHandoff path.
   */
  resolveHandoff: (
    campaignCreatorId: string,
    resolution: "answer" | "dismiss",
    answer: string | undefined,
    token: string
  ) => Promise<boolean>;
  /**
   * Answer Amy's offer task (eve_offer_request) with the creator's fee /
   * revenue share / CPA for the tier she asked about. Amy continues right
   * away. False on failure (the error is toasted verbatim).
   */
  setCreatorOffer: (
    campaignCreatorId: string,
    offer: { max_fee: number | null; revenue_share: number | null; cpa: number | null; keep_campaign_fee: boolean },
    token: string
  ) => Promise<boolean>;
  /** Settings → Messaging: the Offers switch (campaigns.eve_offer_review). */
  setOfferReview: (campaignId: string, enabled: boolean, orgId: string, token: string) => Promise<boolean>;
}

type EveActionPath =
  | "approve-creator"
  | "reject-creator"
  | "resolve-phase-review"
  | "resolve-handoff"
  | "confirm-payout"
  | "cancel-payout"
  | "approve-contract-change"
  | "decline-contract-change";

async function callEveAction(
  path: EveActionPath,
  body: Record<string, unknown>,
  token: string
): Promise<boolean> {
  let data: EveActionResponse;
  try {
    const response = await fetch(`${EVE_HOST}/eve/v1/actions/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    data = (await response.json()) as EveActionResponse;
  } catch {
    toast.error("Could not reach Amy — try again in a moment.");
    return false;
  }

  if (data.ok) {
    if (data.status) {
      useCreatorsStore
        .getState()
        .patchCreator(String(body.campaign_creator_id), { status: data.status });
    }
    if (data.next === "manual_reply" || data.next === "clear_error") {
      // Recorded, but Amy can't finish this one herself — the detail says what to do.
      toast.warning(data.detail ?? "Recorded — Amy needs you to finish this one.");
    } else {
      toast.success(data.detail ?? "Done — Amy is on it.");
    }
    return true;
  }

  if (data.code === "not_handed_off") {
    // Resolved elsewhere already (chat, a second click) — benign.
    toast.info(data.error ?? "This handoff was already resolved.");
    return false;
  }

  if (data.code === "status_conflict") {
    // Double-click / raced decision — benign; sync the row to reality.
    toast.info(
      data.decided
        ? `${DECIDED_LABELS[data.decided] ?? `Already ${data.decided}`} (now ${data.current_status}).`
        : data.error ?? "The creator already moved on."
    );
    if (data.current_status) {
      useCreatorsStore
        .getState()
        .patchCreator(String(body.campaign_creator_id), { status: data.current_status });
    }
    return false;
  }

  if (data.code === "grade_error") {
    toast.error(`${data.error ?? "Grade refused the request."} No money moved.`);
    return false;
  }

  toast.error(data.error ?? "Amy couldn't record the decision.");
  return false;
}

/**
 * Campaign-scoped config writes (PipelineTab): the amy routes run the SAME
 * executors as the chat tools, so validation errors arrive as user-grade
 * text ("Enabling the contract phase requires a DocuSeal template…") —
 * toast them verbatim. On success the campaign is silently re-synced so the
 * editor reflects server truth (defaults stripped, orders merged).
 */
async function callEveCampaignConfig(
  path: EveCampaignConfigPath,
  body: Record<string, unknown>,
  orgId: string,
  token: string,
  successMessage: string
): Promise<boolean> {
  return (await callEveCampaignConfigData(path, body, orgId, token, successMessage)) !== null;
}

type EveCampaignConfigPath =
  | "update-campaign-phases"
  | "set-campaign-autopilot"
  | "set-amy-instructions"
  | "set-campaign-mailbox"
  | "set-offer-review";

/** Same as callEveCampaignConfig, returning the route's JSON (null on failure) for callers that read more than ok. */
async function callEveCampaignConfigData<T extends Record<string, unknown>>(
  path: EveCampaignConfigPath,
  body: Record<string, unknown>,
  orgId: string,
  token: string,
  successMessage: string
): Promise<T | null> {
  let data: T & { ok: boolean; error?: string; warnings?: string[]; detail?: string };
  try {
    const response = await fetch(`${EVE_HOST}/eve/v1/actions/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    data = (await response.json()) as typeof data;
  } catch {
    toast.error("Could not reach Amy — try again in a moment.");
    return null;
  }

  if (!data.ok) {
    toast.error(data.error ?? "Amy couldn't save the change.");
    return null;
  }

  for (const w of data.warnings ?? []) toast.warning(w);
  toast.success(data.detail ?? successMessage);
  await useCampaignStore
    .getState()
    .refreshCampaign(String(body.campaign_id), orgId, token);
  return data;
}

/** set-campaign-mailbox's answer: the counts behind the switch-off re-queue prompt. */
export interface MailboxChangeResult extends Record<string, unknown> {
  /** Switched-off mailbox: creators it cold-emailed who never answered (null otherwise). */
  unreplied_count: number | null;
  /** Creators this call re-queued (null when none asked). */
  requeued: number | null;
}

export const useEveActionsStore = create<EveActionsState>((set) => ({
  isLoading: false,
  isSavingConfig: false,

  approveCreator: async (campaignCreatorId, opts, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "approve-creator",
        {
          campaign_creator_id: campaignCreatorId,
          ...(opts.promo_code ? { promo_code: opts.promo_code } : {}),
          ...(opts.unique_link ? { unique_link: opts.unique_link } : {}),
        },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  rejectCreator: async (campaignCreatorId, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "reject-creator",
        { campaign_creator_id: campaignCreatorId },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  resolvePhaseReview: async (campaignCreatorId, decision, notes, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "resolve-phase-review",
        {
          campaign_creator_id: campaignCreatorId,
          decision,
          ...(notes?.trim() ? { notes: notes.trim() } : {}),
        },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  approveContractChange: async (campaignCreatorId, amendments, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "approve-contract-change",
        { campaign_creator_id: campaignCreatorId, amendments },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  declineContractChange: async (campaignCreatorId, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "decline-contract-change",
        { campaign_creator_id: campaignCreatorId },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  confirmPayout: async (campaignCreatorId, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction("confirm-payout", { campaign_creator_id: campaignCreatorId }, token);
    } finally {
      set({ isLoading: false });
    }
  },

  cancelPayout: async (campaignCreatorId, reason, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "cancel-payout",
        { campaign_creator_id: campaignCreatorId, reason },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  resolveHandoff: async (campaignCreatorId, resolution, answer, token) => {
    set({ isLoading: true });
    try {
      return await callEveAction(
        "resolve-handoff",
        {
          campaign_creator_id: campaignCreatorId,
          resolution,
          ...(answer?.trim() ? { answer: answer.trim() } : {}),
        },
        token
      );
    } finally {
      set({ isLoading: false });
    }
  },

  updateCampaignPhases: async (campaignId, phases, orgId, token, options) => {
    set({ isSavingConfig: true });
    try {
      // `tiers` rides along only when it names tiers — an all-tiers save
      // omits it (the route treats null and omitted alike), so callers that
      // pass no options send exactly the pre-tier body.
      const body: EveUpdateCampaignPhasesBody = { campaign_id: campaignId, phases };
      if (options?.tiers && options.tiers.length > 0) body.tiers = options.tiers;
      if (options?.copy_from_tier != null) body.copy_from_tier = options.copy_from_tier;
      return await callEveCampaignConfig(
        "update-campaign-phases",
        body,
        orgId,
        token,
        options?.copy_from_tier != null ? "Pipeline copied." : "Pipeline updated."
      );
    } finally {
      set({ isSavingConfig: false });
    }
  },

  setOfferReview: async (campaignId, enabled, orgId, token) => {
    set({ isSavingConfig: true });
    try {
      return await callEveCampaignConfig(
        "set-offer-review",
        { campaign_id: campaignId, enabled },
        orgId,
        token,
        enabled ? "Amy waits for each creator's offer." : "Offers off."
      );
    } finally {
      set({ isSavingConfig: false });
    }
  },

  setCampaignAutopilot: async (campaignId, enabled, orgId, token) => {
    set({ isSavingConfig: true });
    try {
      return await callEveCampaignConfig(
        "set-campaign-autopilot",
        { campaign_id: campaignId, enabled },
        orgId,
        token,
        enabled ? "Amy resumed." : "Amy paused."
      );
    } finally {
      set({ isSavingConfig: false });
    }
  },

  setAmyInstructions: async (campaignId, content, orgId, token) => {
    set({ isSavingConfig: true });
    try {
      return await callEveCampaignConfig(
        "set-amy-instructions",
        { campaign_id: campaignId, content },
        orgId,
        token,
        "Instructions saved."
      );
    } finally {
      set({ isSavingConfig: false });
    }
  },

  setCreatorOffer: async (campaignCreatorId, offer, token) => {
    let data: { ok: boolean; error?: string; detail?: string; eve_offer_overrides?: EveOfferOverride[] };
    try {
      const response = await fetch(`${EVE_HOST}/eve/v1/actions/set-creator-offer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId, ...offer }),
      });
      data = (await response.json()) as typeof data;
    } catch {
      toast.error("Could not reach Amy — try again in a moment.");
      return false;
    }
    if (!data.ok) {
      toast.error(data.error ?? "Amy couldn't save the offer.");
      return false;
    }
    useCreatorsStore.getState().patchCreator(campaignCreatorId, {
      eve_offer_overrides: data.eve_offer_overrides ?? [],
      eve_offer_request: null,
    });
    toast.success(data.detail ?? "Offer saved — Amy continues.");
    return true;
  },

  setCampaignMailbox: async (campaignId, change, orgId, token, successMessage) => {
    set({ isSavingConfig: true });
    try {
      return await callEveCampaignConfigData<MailboxChangeResult>(
        "set-campaign-mailbox",
        { campaign_id: campaignId, ...change },
        orgId,
        token,
        successMessage ?? "Mailbox saved."
      );
    } finally {
      set({ isSavingConfig: false });
    }
  },

}));
